import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import QuoteForm from '../../components/base/QuoteForm';
import { useSEO } from '../../utils/seo';

const HomePage = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://danvanremovals.co.uk';
  
  useSEO({
    title: 'Premium Removals Lancashire | Dan & Van - House & Office Moves',
    description: 'Professional, fully insured removals across Lancashire. House moves, office relocations, single items. Get your free quote today — 07530 166035',
    keywords: 'removals Lancashire, house removals, office removals, man and van, furniture moving, single item delivery',
    canonical: '/',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'MovingCompany',
      name: 'Dan & Van Removals',
      image: `${siteUrl}/logo.png`,
      telephone: '07530166035',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Lancashire',
        addressCountry: 'GB',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 53.7632,
        longitude: -2.7044,
      },
      areaServed: {
        '@type': 'GeoCircle',
        geoMidpoint: {
          '@type': 'GeoCoordinates',
          latitude: 53.7632,
          longitude: -2.7044,
        },
        geoRadius: '50000',
      },
      priceRange: '££',
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
          opens: '08:00',
          closes: '18:00',
        },
      ],
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Removals Services',
        itemListElement: [
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'House Removals',
              description: 'Complete home moving service with care and precision',
            },
          },
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'Office Removals',
              description: 'Minimal downtime business relocations',
            },
          },
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'Single Item Moves',
              description: 'Furniture and appliance transportation',
            },
          },
        ],
      },
    },
  });

  const services = [
    {
      icon: 'ri-home-4-line',
      title: 'House Removals',
      description: 'Complete home moving service with care and precision',
      link: '/house-removals',
      image: 'https://static.readdy.ai/image/ab22404d73908d3c144c597ec26057d7/6ab797db9258fe63a07b0ab552e81554.png',
    },
    {
      icon: 'ri-building-line',
      title: 'Office Removals',
      description: 'Minimal downtime business relocations',
      link: '/office-removals',
      image: 'https://static.readdy.ai/image/ab22404d73908d3c144c597ec26057d7/c465c72b941e8e8b042cdd7b515efa09.png',
    },
    {
      icon: 'ri-box-3-line',
      title: 'Single Item Moves',
      description: 'Furniture and appliance transportation',
      link: '/single-item-moves',
      image: 'https://static.readdy.ai/image/ab22404d73908d3c144c597ec26057d7/ef34490b9daf08b7bdef3a98f4fba11a.png',
    },
    {
      icon: 'ri-truck-line',
      title: 'Collections & Deliveries',
      description: 'Reliable pickup and delivery service',
      link: '/collections-deliveries',
      image: 'https://static.readdy.ai/image/ab22404d73908d3c144c597ec26057d7/1c368994cb3584c8f07915f2103eeef9.png',
    },
  ];

  const steps = [
    {
      number: '01',
      title: 'Get a Quote',
      description: 'Tell us about your move and receive a free, no-obligation quote',
    },
    {
      number: '02',
      title: 'We Plan',
      description: 'We arrange everything and confirm your moving day details',
    },
    {
      number: '03',
      title: 'Move Day',
      description: 'Professional team handles your belongings with care',
    },
  ];

  const testimonials = [
    {
      text: 'Exceptional service from start to finish. The team was professional, careful with our belongings, and made our house move completely stress-free.',
      author: 'Sarah M.',
      location: 'Accrington',
      rating: 5,
    },
    {
      text: 'Used Dan & Van for our office relocation. They worked efficiently over the weekend so we had zero downtime. Highly recommend.',
      author: 'James T.',
      location: 'Blackburn',
      rating: 5,
    },
    {
      text: 'Needed a piano moved urgently. They came the next day, handled it with such care, and the price was very reasonable. Brilliant service.',
      author: 'Emma L.',
      location: 'Clayton-le-Moors',
      rating: 5,
    },
  ];

  const areas = [
    'Accrington',
    'Clayton-le-Moors',
    'Blackburn',
    'Burnley',
    'Preston',
    'Lancaster',
    'Chorley',
    'Darwen',
    'Clitheroe',
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://static.readdy.ai/image/ab22404d73908d3c144c597ec26057d7/f18516b3bd3454fddeabf463e91b8816.jpeg')`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>

        <div className="relative z-10 w-full max-w-6xl mx-auto px-6 py-20 text-center">
          {/* Trust Badges */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="grid grid-cols-3 sm:flex sm:flex-wrap items-center justify-center gap-3 sm:gap-4 mb-8 text-white text-xs uppercase tracking-wider"
          >
            <div className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2">
              <i className="ri-shield-check-line text-gold text-lg"></i>
              <span className="text-center">Fully Insured</span>
            </div>
            <span className="text-gold hidden sm:inline">•</span>
            <div className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2">
              <i className="ri-shield-star-line text-gold text-lg"></i>
              <span className="text-center">Protective Equipment</span>
            </div>
            <span className="text-gold hidden sm:inline">•</span>
            <div className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2">
              <i className="ri-time-line text-gold text-lg"></i>
              <span className="text-center">24hr Callback</span>
            </div>
          </motion.div>

          {/* Main Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="font-sans text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-4 sm:mb-6 leading-tight"
          >
            Premium removals,
            <br />
            handled properly.
          </motion.h1>

          {/* Subheadline */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-base sm:text-lg md:text-xl text-white/90 mb-8 sm:mb-12 font-light max-w-2xl mx-auto"
          >
            Stress-free house and office moves across Lancashire
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-12 sm:mb-16"
          >
            <Link
              to="/contact"
              className="w-full sm:w-auto px-8 sm:px-10 py-3 sm:py-4 bg-gold text-charcoal font-semibold text-sm sm:text-base md:text-lg rounded hover:shadow-luxury-hover transition-all whitespace-nowrap cursor-pointer"
            >
              Get a Quote
            </Link>
            <a
              href="tel:07530166035"
              className="w-full sm:w-auto px-8 sm:px-10 py-3 sm:py-4 border-2 border-white text-white font-semibold text-sm sm:text-base md:text-lg rounded hover:bg-white hover:text-charcoal transition-all whitespace-nowrap cursor-pointer"
            >
              Call / WhatsApp
            </a>
          </motion.div>

          {/* Scroll Indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 1 }}
            className="hidden md:block"
          >
            <div className="inline-flex w-6 h-10 border-2 border-gold rounded-full items-start justify-center p-2">
              <motion.div
                animate={{ y: [0, 12, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
                className="w-1 h-2 bg-gold rounded-full"
              ></motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Social Proof Strip */}
      <section className="bg-gray-50 py-6 sm:py-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 sm:gap-6">
            <div className="flex items-center">
              <div className="flex text-gold text-xl sm:text-2xl mr-3">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill"></i>
                ))}
              </div>
              <span className="font-sans text-xl sm:text-2xl font-bold text-charcoal">5.0/5</span>
            </div>
            <p className="text-sm sm:text-base text-gray-600 text-center">
              Trusted by local homeowners & businesses
            </p>
            <Link
              to="/reviews"
              className="text-sm sm:text-base text-charcoal font-medium hover:text-gold transition-colors flex items-center cursor-pointer whitespace-nowrap"
            >
              Read Reviews
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 sm:py-20 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="mb-12 sm:mb-16">
            <div className="text-gold text-xs uppercase tracking-wider mb-3">What We Do</div>
            <h2 className="font-sans text-3xl sm:text-4xl md:text-5xl font-bold text-charcoal">
              Moving services designed around you
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Link
                  to={service.link}
                  className="block bg-white border border-gold/20 rounded-lg overflow-hidden hover:-translate-y-2 hover:shadow-luxury transition-all duration-300 cursor-pointer h-full group"
                >
                  <div className="relative h-48 w-full overflow-hidden">
                    <div
                      className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                      style={{ backgroundImage: `url('${service.image}')` }}
                    ></div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    <div className="absolute bottom-4 left-4 w-10 h-10 flex items-center justify-center bg-gold rounded-full">
                      <i className={`${service.icon} text-2xl text-charcoal`}></i>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="font-sans text-xl font-bold text-charcoal mb-3">
                      {service.title}
                    </h3>
                    <p className="text-gray-600 text-sm mb-4">{service.description}</p>
                    <span className="text-gold text-sm font-medium flex items-center">
                      Learn more
                      <i className="ri-arrow-right-line ml-2"></i>
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 sm:py-20 md:py-24 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <h2 className="font-sans text-3xl sm:text-4xl md:text-5xl font-bold text-charcoal text-center mb-16 sm:mb-20">
            How it works
          </h2>

          <div className="relative">
            {/* Timeline Line */}
            <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-0.5 bg-gold/30 transform -translate-y-1/2"></div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-8">
              {steps.map((step, index) => (
                <motion.div
                  key={step.number}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.2 }}
                  className="relative text-center"
                >
                  {/* Number Circle */}
                  <div className="relative inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-white border-4 border-gold rounded-full mb-6 z-10">
                    <span className="font-sans text-2xl sm:text-3xl font-light text-gold">
                      {step.number}
                    </span>
                  </div>

                  <h3 className="font-sans text-xl sm:text-2xl font-bold text-charcoal mb-4">
                    {step.title}
                  </h3>
                  <p className="text-sm sm:text-base text-gray-600">{step.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Insurance & Care Section - WITH PHOTOGRAPHY */}
      <section className="py-16 sm:py-20 md:py-24 bg-charcoal text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url('https://public.readdy.ai/ai/img_res/18f210784be8ba471ca411ffa0d947b0.jpg')`,
            }}
          ></div>
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-charcoal via-charcoal/95 to-charcoal/90"></div>
        
        <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 sm:gap-12 items-center">
            <div className="lg:col-span-2">
              <h2 className="font-sans text-3xl sm:text-4xl md:text-5xl font-bold mb-6 leading-tight">
                Protected.
                <br />
                Professional.
                <br />
                Proven.
              </h2>
              <div className="w-16 h-1 bg-gold"></div>
            </div>

            <div className="lg:col-span-3 space-y-6">
              <div className="flex items-start">
                <i className="ri-checkbox-circle-fill text-gold text-xl sm:text-2xl mr-4 mt-1 flex-shrink-0"></i>
                <div>
                  <h3 className="text-lg sm:text-xl font-semibold mb-2">Fully Insured</h3>
                  <p className="text-sm sm:text-base text-gray-300">
                    Comprehensive goods-in-transit and public liability insurance for complete peace of mind
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <i className="ri-checkbox-circle-fill text-gold text-xl sm:text-2xl mr-4 mt-1 flex-shrink-0"></i>
                <div>
                  <h3 className="text-lg sm:text-xl font-semibold mb-2">Protective Equipment</h3>
                  <p className="text-sm sm:text-base text-gray-300">
                    Professional-grade blankets, straps, trolleys, and wrapping materials
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <i className="ri-checkbox-circle-fill text-gold text-xl sm:text-2xl mr-4 mt-1 flex-shrink-0"></i>
                <div>
                  <h3 className="text-lg sm:text-xl font-semibold mb-2">Careful Handling</h3>
                  <p className="text-sm sm:text-base text-gray-300">
                    Experienced team trained in proper lifting and transportation techniques
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Carousel */}
      <section className="py-16 sm:py-20 md:py-24 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12 sm:mb-16">
            <div className="text-gold text-xs uppercase tracking-wider mb-3">Testimonials</div>
            <h2 className="font-sans text-3xl sm:text-4xl md:text-5xl font-bold text-charcoal">
              What our customers say
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white p-6 sm:p-8 rounded-lg shadow-luxury"
              >
                <div className="flex text-gold text-base sm:text-lg mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill"></i>
                  ))}
                </div>
                <p className="font-sans text-base sm:text-lg text-charcoal italic mb-6 leading-relaxed">
                  "{testimonial.text}"
                </p>
                <div className="text-sm text-gray-600">
                  <strong className="text-charcoal">{testimonial.author}</strong>
                  <br />
                  {testimonial.location}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Areas We Cover - WITH PHOTOGRAPHY */}
      <section className="py-16 sm:py-20 md:py-24 bg-white relative">
        <div className="absolute right-0 top-0 bottom-0 w-1/2 hidden lg:block">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url('https://readdy.ai/api/search-image?query=aerial%20view%20Lancashire%20countryside%20with%20towns%20and%20villages%20rolling%20hills%20green%20landscape%20roads%20connecting%20communities%20beautiful%20British%20scenery%20natural%20daylight%20premium%20quality&width=1200&height=1600&seq=areas001&orientation=portrait')`,
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-white via-white/80 to-transparent"></div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
          <div className="lg:w-1/2">
            <div className="mb-8 sm:mb-12">
              <div className="text-gold text-xs uppercase tracking-wider mb-3">Coverage</div>
              <h2 className="font-sans text-3xl sm:text-4xl md:text-5xl font-bold text-charcoal mb-4 sm:mb-6">
                Serving Lancashire & beyond
              </h2>
              <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
                We provide professional removals services across Lancashire and surrounding areas. From Accrington to Preston, we know the local roads and deliver reliable, stress-free moves.
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 sm:gap-x-8 gap-y-3 mb-8 sm:mb-10">
              {areas.map((area, index) => (
                <motion.div
                  key={area}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  className="flex items-center"
                >
                  <i className="ri-map-pin-line text-gold text-base sm:text-lg mr-2 flex-shrink-0"></i>
                  <span className="font-medium text-charcoal text-sm sm:text-base">{area}</span>
                </motion.div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <Link
                to="/areas-we-cover"
                className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-gold text-charcoal font-semibold text-sm sm:text-base rounded hover:shadow-luxury-hover transition-all whitespace-nowrap cursor-pointer inline-flex items-center justify-center"
              >
                View All Areas
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
              <p className="text-xs sm:text-sm text-gray-600">
                Don't see your area? <Link to="/contact" className="text-gold hover:underline cursor-pointer">Get in touch</Link>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Band - WITH PHOTOGRAPHY */}
      <section className="py-16 sm:py-20 md:py-24 bg-white border-t-4 border-gold relative overflow-hidden">
        <div className="absolute left-0 top-0 bottom-0 w-1/3 hidden lg:block">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url('https://static.readdy.ai/image/ab22404d73908d3c144c597ec26057d7/b6f80006fba64b10f1ed4ed0888d2f38.jpeg')`,
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-white"></div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">
            <div className="lg:col-start-2">
              <h2 className="font-sans text-3xl sm:text-4xl md:text-5xl font-bold text-charcoal mb-4">
                Ready to move?
              </h2>
              <p className="text-lg sm:text-xl text-gray-600 mb-4 sm:mb-6">
                Get your free quote in under 60 seconds
              </p>
              <div className="flex items-center text-gold mb-6 sm:mb-8">
                <i className="ri-check-line text-xl sm:text-2xl mr-2"></i>
                <span className="font-medium text-sm sm:text-base">We'll respond within 24 hours</span>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-6 sm:mb-8">
                <a
                  href="tel:07530166035"
                  className="px-6 sm:px-8 py-3 sm:py-4 border-2 border-charcoal text-charcoal font-semibold text-sm sm:text-base rounded hover:border-gold hover:text-gold transition-all text-center whitespace-nowrap cursor-pointer"
                >
                  Call 07530 166035
                </a>
                <a
                  href="https://wa.me/447530166035"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-6 sm:px-8 py-3 sm:py-4 bg-green-600 text-white font-semibold text-sm sm:text-base rounded hover:bg-green-700 transition-all flex items-center justify-center whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-whatsapp-line text-lg sm:text-xl mr-2"></i>
                  WhatsApp Us
                </a>
              </div>

              <p className="text-sm text-gray-600 mb-6">
                Prefer to call? Reach us at <a href="tel:07530166035" className="text-gold hover:underline cursor-pointer">07530 166035</a>
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
